import React, { useMemo, useState } from "react";
import { Accordion, Table } from "react-bootstrap";
import "./GetFeatureInfo.css";

const GetFeatureInfo = ({
    selectedFeatures = [],
    onRemoveSelectedFeature,
    renderActions,
}) => {
    const [openItems, setOpenItems] = useState({});
    const [parentKey, setParentKey] = useState("0");

    const toggleAccordion = (index) => {
        setOpenItems((prev) => ({
            ...prev,
            [index]: !prev[index],
        }));
    };

    const idMapping = {
        stubovi: "id_stubnog_mjesta",
        vod: "id_vod",
        dionica: "id_dionica",
        trafo_stanice: "id_trafo_stanice",
        mjerna_mjesta: "id_mm",
    };

    const parseFeatureId = (featureId = "") => {
        return featureId.split(".")[0];
    };

    const getFeatureType = (featureId = "") => {
        if (featureId.includes("trafo_stanice")) return "trafo_stanice";
        if (featureId.includes("mjerna_mjesta")) return "mjerna_mjesta";
        if (featureId.includes("stubovi")) return "stubovi";
        if (featureId.includes("vod")) return "vod";
        if (featureId.includes("dionica")) return "dionica";
        return "ostalo";
    };

    const panelTitle = useMemo(() => {
        if (!selectedFeatures.length) return "Podaci o odabranim objektima";

        const types = [
            ...new Set(selectedFeatures.map((f) => getFeatureType(f?.id || ""))),
        ];

        const hasOnly = (type) => types.length === 1 && types[0] === type;

        const hasVodOrDionica =
            types.every((t) => t === "vod" || t === "dionica") &&
            types.some((t) => t === "vod" || t === "dionica");

        if (hasOnly("trafo_stanice")) {
            return "Podaci o odabranim trafostanicama";
        }

        if (hasOnly("mjerna_mjesta")) {
            return "Podaci o odabranim mjernim mjestima";
        }

        if (hasOnly("stubovi")) {
            return "Podaci o odabranim stubovima";
        }

        if (hasVodOrDionica) {
            return "Podaci o odabranim vodovima i dionicama";
        }

        return "Podaci o odabranim objektima";
    }, [selectedFeatures]);

    const handleRemoveFeature = (e, featureGroup, index) => {
        e.preventDefault();
        e.stopPropagation();

        if (typeof onRemoveSelectedFeature === "function") {
            onRemoveSelectedFeature(featureGroup, index);
        }
    };

    return (
        <div className="feature-info-panel">
            <Accordion
                activeKey={parentKey}
                onSelect={(key) => setParentKey(key === parentKey ? null : key)}
            >
                <Accordion.Item eventKey="0">
                    <Accordion.Header>{panelTitle}</Accordion.Header>

                    <Accordion.Body>
                        {selectedFeatures.length === 0 ? (
                            <div className="feature-info-empty">
                                Nema odabranih objekata.
                            </div>
                        ) : (
                            <Accordion alwaysOpen>
                                {selectedFeatures.map((featureGroup, index) => {
                                    const matchedKey = Object.keys(idMapping).find((key) =>
                                        featureGroup?.id?.includes(key)
                                    );

                                    const layerName = parseFeatureId(
                                        featureGroup?.id || ""
                                    );

                                    const objectId = matchedKey
                                        ? featureGroup?.properties?.[
                                        idMapping[matchedKey]
                                        ]
                                        : "nepoznato";

                                    const headerText = `ID (${layerName}) - ${objectId ?? "nepoznato"
                                        }`;

                                    return (
                                        <Accordion.Item
                                            eventKey={index.toString()}
                                            key={`${featureGroup?.id || "feature"}-${index}`}
                                        >
                                            <Accordion.Header
                                                onClick={() => toggleAccordion(index)}
                                            >
                                                <div className="feature-item-header">
                                                    <span className="feature-item-title">
                                                        {headerText}
                                                    </span>

                                                    <div className="feature-item-actions">
                                                        {typeof renderActions ===
                                                            "function" ? (
                                                            renderActions(
                                                                featureGroup,
                                                                index
                                                            )
                                                        ) : (
                                                            <button
                                                                type="button"
                                                                className="feature-remove-btn"
                                                                onClick={(e) =>
                                                                    handleRemoveFeature(
                                                                        e,
                                                                        featureGroup,
                                                                        index
                                                                    )
                                                                }
                                                                title="Ukloni iz odabira"
                                                                aria-label="Ukloni iz odabira"
                                                            >
                                                                ×
                                                            </button>
                                                        )}
                                                    </div>
                                                </div>
                                            </Accordion.Header>

                                            {openItems[index] && (
                                                <Accordion.Body>
                                                    <Table
                                                        striped
                                                        bordered
                                                        hover
                                                        className="feature-info-table"
                                                    >
                                                        <tbody>
                                                            {Object.entries(
                                                                featureGroup?.properties ||
                                                                {}
                                                            )
                                                                .filter(
                                                                    ([, value]) =>
                                                                        value !== null &&
                                                                        value !==
                                                                        undefined &&
                                                                        value !== ""
                                                                )
                                                                .map(([key, value]) => (
                                                                    <tr key={key}>
                                                                        <td className="key-column">
                                                                            <strong>
                                                                                {key}
                                                                            </strong>
                                                                        </td>
                                                                        <td className="value-column">
                                                                            {String(value)}
                                                                        </td>
                                                                    </tr>
                                                                ))}
                                                        </tbody>
                                                    </Table>
                                                </Accordion.Body>
                                            )}
                                        </Accordion.Item>
                                    );
                                })}
                            </Accordion>
                        )}
                    </Accordion.Body>
                </Accordion.Item>
            </Accordion>
        </div>
    );
};

export default GetFeatureInfo;